import React, { Component } from "react"
import { isValidElementType } from "react-is"
import each from "lodash/each"
import PropTypes from "prop-types"
import Events from "events"

/* eslint react/sort-comp: 0 */
/* Because it has masochist sort logic */
const getWorkareaWrapper = (BaseComponent, { to = `workspace` } = {}) =>
  class Workspace extends Component {
    static childContextTypes = {
      workspace: PropTypes.shape({
        capture: PropTypes.func.isRequired,
        uncapture: PropTypes.func.isRequired,
        addLabel: PropTypes.func.isRequired,
        removeLabel: PropTypes.func.isRequired,
      }),
    }

    static contextTypes = {
      workspace: PropTypes.shape({
        capture: PropTypes.func.isRequired,
        uncapture: PropTypes.func.isRequired,
        addLabel: PropTypes.func.isRequired,
        removeLabel: PropTypes.func.isRequired,
      }),
    }

    /* Api will be added to props flow */
    api = {
      /* Capture event, propagated from top to the bottom */
      capture: (eventName, handler) => {
        const { workspace } = this.context

        this.emitter.addListener(eventName, handler)
        if (workspace) {
          workspace.capture(eventName, handler)
        }

        if (!this.emitterComposableDisposable[eventName]) {
          this.emitterComposableDisposable[eventName] = []
        }
        if (!this.emitterComposableDisposable[eventName].includes(handler)) {
          this.emitterComposableDisposable[eventName].push(handler)
        }
      },
      /* Stop capture event, propagated from top to the bottom */
      uncapture: (eventName, handler) => {
        const { workspace } = this.context

        this.emitter.removeListener(eventName, handler)
        if (workspace) {
          workspace.uncapture(eventName, handler)
        }
      },
      /* Emit event, which will be propagated from top to bottom */
      emit: eventName => {
        this.emitter.emit(eventName)
      },

      /* Label will be added to each parent workspace */
      addLabel: (labelName, count = 1) => {
        const { workspace } = this.context

        this.setNextState({
          ...this.nextState,
          labels: {
            ...this.nextState.labels,
            [labelName]: (this.nextState.labels[labelName] || 0) + count,
          },
        })

        if (workspace) {
          workspace.addLabel(labelName, count)
        }
      },

      /* Remote label from each parent workspace */
      removeLabel: (labelName, count = 1) => {
        const { workspace } = this.context

        if (!this.nextState.labels[labelName]) {
          return
        }

        let nextLabelCount = this.nextState.labels[labelName] - count
        if (nextLabelCount < 0) {
          nextLabelCount = 0
        }
        this.setNextState({
          ...this.nextState,
          labels: {
            ...this.nextState.labels,
            [labelName]: nextLabelCount,
          },
        })

        if (workspace) {
          workspace.removeLabel(labelName)
        }
      },
    }

    nextState = {
      labels: {},
    }

    state = {
      ...this.api,
      ...this.nextState,
    }

    getChildContext() {
      return {
        workspace: this.api,
      }
    }

    componentWillUnmount() {
      const { workspace } = this.context

      if (workspace) {
        each(this.emitterComposableDisposable, (handlers, eventName) => {
          each(handlers, handler => workspace.uncapture(eventName, handler))
        })
      } else {
        this.emitter.removeAllListeners()
      }
      if (workspace) {
        /* Remove all labels from parent context */
        Object.keys(this.nextState.labels).forEach((labelName, count) => {
          workspace.removeLabel(labelName, count)
        })
      }
    }

    componentDidUpdate() {
      this.emitter.emit(`workspace.update`, this.state)
    }

    setNextState(nextState) {
      this.nextState = nextState
      this.setState({
        ...this.api,
        ...this.nextState,
      })
    }

    emitter = new Events()

    emitterComposableDisposable = {}

    render() {
      const extraProps = {
        [to]: this.state,
      }

      return <BaseComponent {...this.props} {...extraProps} />
    }
  }

export default function workspaceHoc(payload) {
  if (isValidElementType(payload)) {
    // Without custom config
    return getWorkareaWrapper(payload)
  }
  // With custom config
  return BaseComponent => getWorkareaWrapper(BaseComponent, payload)
}
